import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Space, Table, Tabs } from "antd";
import axios from "axios";
import React, { useRef, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";

const StudentParentList = () => {
  const { Search } = Input;
  const navigate = useNavigate();
  const [selectedClass, setSelectedClass] = useState("All");
  const [search, setSearch] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const handleSelectChange = (id) => {
    setSelectedClass(id);
  };

  // fetch a single class's student+parent list
  const fetchParentStudentList = async (classId) => {
    const res = await axios.get(
      serverUrl + "/students/class/" + classId + "/parent/",
      {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      }
    );

    return res.data;
  };

  const parentStudentList = useQuery(
    ["class", selectedClass],
    () => fetchParentStudentList(selectedClass),
    {
      enabled: selectedClass !== "All",
    }
  );

  // fetch classes
  async function fetchClasses() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const classes = useQuery("classes", fetchClasses);

  // "All" tab: fetch every class's list in parallel and merge them, since
  // the backend only exposes a per-class endpoint.
  const allParentStudentList = useQuery(
    ["class", "All", classes.data],
    async () => {
      const responses = await Promise.all(
        classes.data.map((c) => fetchParentStudentList(c._id)),
      );
      return responses.flatMap((res) => res?.result || []);
    },
    {
      enabled: selectedClass === "All" && classes.isSuccess,
    }
  );

  const isAllTab = selectedClass === "All";
  const parentRecords = isAllTab
    ? allParentStudentList?.data || []
    : parentStudentList?.data?.result || [];
  const isListLoading = isAllTab
    ? allParentStudentList?.isLoading
    : parentStudentList?.isLoading;

  const filteredParentRecords = search
    ? parentRecords.filter((record) => {
        const studentName = `${record?.first_name || ""} ${
          record?.last_name || ""
        }`.toLowerCase();
        const parentName = `${record?.parent?.first_name || ""} ${
          record?.parent?.last_name || ""
        }`.toLowerCase();
        return (
          studentName.includes(search.toLowerCase()) ||
          parentName.includes(search.toLowerCase())
        );
      })
    : parentRecords;

  // Table Settings

  // Search Filter

  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInputRef = useRef(null);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{ width: 90 }}
            className="inline-flex items-center justify-center"
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100);
      }
    },
    render: (text) => (searchedColumn === dataIndex ? text : text),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const columns = [
    {
      title: "Student Id",
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center"
        >
          {record?.unique_id}
        </div>
      ),
    },
    {
      title: "Name",
      key: "",
      align: "center",
      ...getColumnSearchProps(["first_name", "last_name"]),
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {`${record?.first_name} ${record?.last_name}`}
        </div>
      ),
      onFilter: (value, record) =>
        (
          record?.first_name?.toLowerCase() +
          " " +
          record?.last_name?.toLowerCase()
        ).includes(value.toLowerCase()),
    },
    {
      title: "Class",
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center"
        >
          {record?.class?.name}
        </div>
      ),
    },
    {
      title: "Parent",
      dataIndex: [],
      ...getColumnSearchProps(["first_name", "last_name"]),
      render: (record) => (
        <div style={{ color: "#616161" }} className=" text-base capitalize">
          {record?.parent
            ? `${record?.parent?.first_name} ${record?.parent?.last_name}`
            : "-"}
        </div>
      ),
      onFilter: (value, record) =>
        (
          record?.parent?.first_name?.toLowerCase() +
          " " +
          record?.parent?.last_name?.toLowerCase()
        ).includes(value.toLowerCase()),
    },
    {
      title: "Sibling",
      dataIndex: [],
      key: "",
      render: (record) => (
        <div
          style={{ color: "#616161" }}
          className=" text-base cursor-pointer"
          onClick={() => {
            if (record?.existing_siblings?.length > 0) {
              setSelectedRecord(record?.existing_siblings);
              setIsModalVisible(true);
            }
          }}
        >
          {record?.existing_siblings?.length}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          className=" text-base font-semibold text-center text-blue-500 cursor-pointer"
          onClick={() =>
            navigate(`/dashboard/admin/menu/parent/${record?._id}`)
          }
        >
          View
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="flex items-center justify-between">
            <div className="text-2xl text-orange-500 font-semibold">
              Parent
            </div>
            <div className="flex flex-row items-center">
              <Search
                value={search}
                placeholder="input search text"
                enterButton="Search"
                loading={false}
                style={{ backgroundColor: "#E3E3E3" }}
                prefix={<BiSearch className="text-gray-600 " />}
                onSearch={(e) => setSearch(e)}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="pt-4">
            <Tabs
              activeKey={selectedClass}
              onChange={(key) => handleSelectChange(key)}
            >
              <Tabs.TabPane tab="All" key="All" />
              {classes.isSuccess &&
                classes.data.map((c) => (
                  <Tabs.TabPane tab={c.name} key={c._id} />
                ))}
            </Tabs>
          </div>
          <div className="py-5">
            <Table
              columns={columns}
              dataSource={filteredParentRecords}
              pagination={true}
              loading={isListLoading}
              rowKey={(record) => record._id}
            />
          </div>
        </div>
        <Modal
          title="Siblings"
          visible={isModalVisible}
          onOk={() => setIsModalVisible(false)}
          onCancel={() => setIsModalVisible(false)}
        >
          <ul>
            {selectedRecord?.map((sibling, index) => (
              <li key={sibling?.id}>
                {index + 1}.{""}
                {`${sibling?.name}`}
              </li>
            ))}
          </ul>
        </Modal>
      </div>
    </>
  );
};

export default StudentParentList;
